import { ClientConfig } from '@/types';
import {
  Card,
  DatePicker,
  Descriptions,
  message,
  Radio,
  Select,
  Spin,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../../../UserContext';
import { HWButton } from '../../../../components/shared/StyledButton';

dayjs.extend(utc);

const { Title } = Typography;
const { Option } = Select;

// Define available pipeline stages
const PIPELINE_STAGES = [
  { value: 'extract_data', label: 'Extract Data' },
  { value: 'recordings', label: 'Recordings' },
  { value: 'process_raw', label: 'Process Raw' },
  { value: 'update', label: 'Update' },
  { value: 'smart_tasks', label: 'Smart Tasks' },
];

export default function BackfillStatusPage({ apiUrl }: { apiUrl: string }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const user = useContext(UserContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [rerunLoading, setRerunLoading] = useState<boolean>(false);
  const [taskMessage, setTaskMessage] = useState('');
  const [client, setClient] = useState<ClientConfig | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [jobMetadata, setJobMetadata] = useState<any>(null);
  const [backfillRunning, setBackfillRunning] = useState(true);
  const [backfillFailed, setBackfillFailed] = useState(false);
  const [selectedStage, setSelectedStage] = useState('extract_data');
  const [errorDetails, setErrorDetails] = useState<any>(null);
  const [backfillStatus, setBackfillStatus] = useState<any>(null);
  const [executionMode, setExecutionMode] = useState<'batch' | 'container'>(
    'batch',
  );
  const [scheduledFor, setScheduledFor] = useState<Dayjs | null>(null);

  const formatDate = (dateString: string | null) => {
    return dateString
      ? dayjs.utc(dateString).local().format('MMMM D, YYYY h:mm A')
      : 'N/A';
  };

  const fetchClient = async () => {
    setLoading(true);
    try {
      if (user == null) {
        return;
      }
      const response = await fetch(`${apiUrl}/api/clients/${clientId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch client');
      }
      const data = await response.json();
      setClient(data);
      setCreatedAt(formatDate(data.created_at));
      setUpdatedAt(formatDate(data.updated_at));
    } catch (error) {
      await message.error('Failed to load client. Please try again later.');
      console.error('Error fetching client:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRerunBackfill = async () => {
    try {
      setRerunLoading(true);
      if (!user || !client?.id) {
        return;
      }

      // Build query parameters
      const params = new URLSearchParams({
        target: selectedStage,
        execution_mode: executionMode,
      });

      // Add start_at only if a date is selected
      if (scheduledFor) {
        params.append('start_at', scheduledFor.utc().format());
      }

      const response = await fetch(
        `${apiUrl}/api/clients/${client.id}/backfill?${params.toString()}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to rerun backfill');
      }

      const data = await response.json();

      // Reset status flags
      setBackfillRunning(true);
      setBackfillFailed(false);

      // Show different message based on whether it was scheduled or started immediately
      if (data.status === 'scheduled' && data.scheduled_for) {
        const scheduledTime = dayjs
          .utc(data.scheduled_for)
          .local()
          .format('MMMM D, YYYY h:mm A');
        await message.success(`Backfill scheduled for ${scheduledTime}`);
      } else {
        await message.success('Backfill started successfully');
      }

      // Refresh status immediately
      await fetchBackfillStatus();
    } catch (error) {
      console.error('Error rerunning backfill:', error);
      await message.error('Failed to rerun backfill. Please try again.');
    } finally {
      setRerunLoading(false);
    }
  };

  const fetchBackfillStatus = async () => {
    if (!client?.client_id) {
      return;
    }
    try {
      if (user == null) {
        return;
      }
      const response = await fetch(
        `${apiUrl}/api/Batches/${client.client_id}/status`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error('Failed to fetch status');
      }
      const data = await response.json();
      setBackfillStatus(data);
      console.log('Backfill failed:', data);
      // Check for failed state
      if (data.state === 'failed') {
        setBackfillRunning(false);
        setBackfillFailed(true);
        setTaskMessage(
          'Backfill process failed. You can rerun it from a specific stage.',
        );

        if (data.error_details) {
          setErrorDetails(data.error_details);
        }
      }
      // Check for completed state
      else if (data.state === 'completed') {
        setBackfillRunning(false);
        setBackfillFailed(false);
        await fetchClient();
      }
      // Check for scheduled state (scheduled backfill)
      else if (data.state === 'scheduled' || data.status === 'scheduled') {
        setBackfillRunning(true);
        setBackfillFailed(false);
      }
      // Check for other non-running states
      else if (
        data.state !== 'running' &&
        data.state !== 'preparing' &&
        data.state !== 'active' &&
        data.state !== 'started' &&
        data.state !== 'scheduled' &&
        data.status !== 'scheduled'
      ) {
        setBackfillRunning(false);
      }

      // Store job metadata if available
      if (data.job_metadata) {
        setJobMetadata(data.job_metadata);
        // Pre-select the last used stage for convenience
        if (data.job_metadata.target_stage) {
          setSelectedStage(data.job_metadata.target_stage);
        }
      }

      setCreatedAt(formatDate(data.created_at));
      setUpdatedAt(formatDate(data.updated_at));
    } catch (error) {
      console.error('Error fetching backfill status:', error);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      void fetchBackfillStatus();
    }, 300000); // 300 seconds
    void fetchBackfillStatus(); // Initial fetch

    return () => {
      clearInterval(intervalId); // Cleanup on unmount
    };
  }, [apiUrl, client?.client_id]);

  useEffect(() => {
    if (backfillRunning) {
      return;
    }
    if (client && client.client_state === 'active') {
      navigate(`/client/${client.id}/settings`);
    } else {
      console.log('backfillRunning else ', backfillRunning);
      setTaskMessage('Something went wrong in the backfill');
    }
  }, [client, backfillRunning, backfillFailed]);

  useEffect(() => {
    void fetchClient();
  }, [apiUrl, clientId]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!client) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={4}>Client not found</Title>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Card>
        <Descriptions
          column={1}
          title={`Backfill Status for ${client.friendly_name}`}
        >
          <Descriptions.Item label="Internal Name">
            {client.name}
          </Descriptions.Item>
          <Descriptions.Item label="Client Id">
            {client.client_id}
          </Descriptions.Item>
          <Descriptions.Item label="Backfill Status">
            {backfillStatus?.status === 'scheduled' ||
            backfillStatus?.state === 'scheduled'
              ? `Scheduled for ${formatDate(backfillStatus.scheduled_for)}`
              : backfillRunning
                ? 'Running'
                : 'Finished with errors'}
          </Descriptions.Item>
          <Descriptions.Item label="Job Started">{createdAt}</Descriptions.Item>
          <Descriptions.Item label="Last Checked">
            {updatedAt}
          </Descriptions.Item>
          <Descriptions.Item label="Message">{taskMessage}</Descriptions.Item>

          {!backfillRunning && jobMetadata && jobMetadata.job_id && (
            <Descriptions.Item label="Log Path">
              {`${jobMetadata.job_id}/extract_data/stderr.txt`}
            </Descriptions.Item>
          )}
        </Descriptions>
        {(!backfillRunning || backfillFailed) && (
          <div style={{ marginTop: '20px' }}>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <div>
                <div
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Execution Mode
                </div>
                <Radio.Group
                  onChange={e => setExecutionMode(e.target.value)}
                  value={executionMode}
                >
                  <Radio value="batch">Batch</Radio>
                  <Radio value="container">Container</Radio>
                </Radio.Group>
              </div>
              <div>
                <div
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Schedule For (Optional) - PST
                </div>
                <DatePicker
                  disabledDate={current =>
                    current && current < dayjs().startOf('minute')
                  }
                  format="MMMM D, YYYY h:mm A"
                  onChange={value => setScheduledFor(value)}
                  placeholder="Select date and time (PST)"
                  showTime={{ format: 'h:mm A', use12Hours: true }}
                  style={{ width: 300 }}
                  value={scheduledFor}
                />
                <div
                  style={{
                    marginTop: '4px',
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.45)',
                  }}
                >
                  Leave empty to start immediately. For Sierra clients, schedule
                  after 11pm PST.
                </div>
              </div>
              <div>
                <div
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Target Stage
                </div>
                <Select
                  onChange={value => setSelectedStage(value)}
                  style={{ width: 200 }}
                  value={selectedStage}
                >
                  {PIPELINE_STAGES.map(stage => (
                    <Option key={stage.value} value={stage.value}>
                      {stage.label}
                    </Option>
                  ))}
                </Select>
                <div
                  style={{
                    marginTop: '4px',
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.45)',
                  }}
                >
                  Select the stage where you want to restart the backfill
                  process
                </div>
              </div>
              <div>
                <HWButton
                  loading={rerunLoading}
                  onClick={handleRerunBackfill}
                  size="large"
                  type="primary"
                >
                  {scheduledFor ? 'Schedule Backfill' : 'Rerun Backfill'}
                </HWButton>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
